import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";

import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Select, FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";

import { DataFormat, Language } from "imports/Utils";

import { DefaultStatusSettingsFormSchema } from "view/statuses/utils/schema";

import { StatusFragmentFragment as StatusFragment } from "__generated__/view/statuses/graphql/fragments.generated";
import { GetStatusSettingsDocument, GetStatusSettingsQuery } from "__generated__/view/statuses/graphql/queries.generated";
import { SaveStatusSettingsDocument, SaveStatusSettingsMutation } from "__generated__/view/statuses/graphql/mutations.generated";
import { StatusType } from "__generated__/enums";

type DefaultStatusSettingsFormInput = z.input<typeof DefaultStatusSettingsFormSchema>;
type DefaultStatusSettingsFormOutput = z.output<typeof DefaultStatusSettingsFormSchema>;

const DefaultStatusSettingsDialog = ({ open, onClose, statuses }: {
	open: boolean;
	onClose: () => void;
	statuses?: StatusFragment[];
}) => {

	const session = useStoreState(state => state.session);

	const [ saveStatusSettings, { loading: submitting } ] = useMutation<SaveStatusSettingsMutation>(SaveStatusSettingsDocument);
	const [ getStatusSettings, { loading, refetch } ] = useLazyQuery<GetStatusSettingsQuery>(GetStatusSettingsDocument);

	const defaultValues: DefaultStatusSettingsFormInput = {
		cargoDefault: null,
		voyageDefault: null,
		callDefault: null,
		declarationDefault: null,
		declarationDeclared: null,
		invoiceDefault: null,
		callVoyageInProgressDefault: null,
		callAtaDefault: null,
		callAtdDefault: null,
		callAtnDefault: null,
		callDtnDefault: null,
		cargoPlannedDefault: null,
		voyageOrderDefault: null,
		afterDeclaringVoyage: null,
	};

	const { control, handleSubmit, reset } = useForm<DefaultStatusSettingsFormInput, unknown, DefaultStatusSettingsFormOutput>({
		resolver: zodResolver(DefaultStatusSettingsFormSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		refetch().then(() => reset(defaultValues));
	};

	const StatusSelect = ({ value, onChange, type }: { value: number | null, onChange: (id: number | null) => void, type: StatusType }) => {
		return (
			<Select
				value={statuses?.find(status => status.id === value)}
				fetchOptions={[ ...statuses || [] ].filter(status => status.type === type)}
				onChange={option => onChange(option?.id ?? null)}
				getOptionLabel={(option) => DataFormat.status(option, session.locale) } />
		);
	};

	const save = (data: DefaultStatusSettingsFormOutput) => {
		saveStatusSettings({
			variables: {
				input: data,
			},
		}).then(() => {
			handleOnClose();
		});
	};

	useEffect(() => {
		if (open) {
			getStatusSettings().then(({ data }) => {
				reset({
					cargoDefault: data?.statusSettings?.cargoDefault?.id ?? null,
					voyageDefault: data?.statusSettings?.voyageDefault?.id ?? null,
					callDefault: data?.statusSettings?.callDefault?.id ?? null,
					declarationDefault: data?.statusSettings?.declarationDefault?.id ?? null,
					declarationDeclared: data?.statusSettings?.declarationDeclared?.id ?? null,
					invoiceDefault: data?.statusSettings?.invoiceDefault?.id ?? null,
					callVoyageInProgressDefault: data?.statusSettings?.callVoyageInProgressDefault?.id ?? null,
					callAtaDefault: data?.statusSettings?.callAtaDefault?.id ?? null,
					callAtdDefault: data?.statusSettings?.callAtdDefault?.id ?? null,
					callAtnDefault: data?.statusSettings?.callAtnDefault?.id ?? null,
					callDtnDefault: data?.statusSettings?.callDtnDefault?.id ?? null,
					cargoPlannedDefault: data?.statusSettings?.cargoPlannedDefault?.id ?? null,
					voyageOrderDefault: data?.statusSettings?.voyageOrderDefault?.id ?? null,
					afterDeclaringVoyage: data?.statusSettings?.afterDeclaringVoyage?.id ?? null,
				} satisfies DefaultStatusSettingsFormInput);
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "STATUS_SETTINGS" })}
			form='statusSettingsForm'
			loading={loading || submitting}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="statusSettingsForm">
				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_CARGO")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="cargoDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Cargo} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_VOYAGE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="voyageDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Voyage} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_CALL")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="callDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Call} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_DECLARATION")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="declarationDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Declaration} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DECLARATION_DECLARED")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="declarationDeclared"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Declaration} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_INVOICE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="invoiceDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Invoice} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_CALL_VOYAGE_IN_PROGRESS")} labelPlacement="left-center" overflow="visible">
					<Controller
						name="callVoyageInProgressDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Call} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_CALL_ATA")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="callAtaDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Call} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_CALL_ATD")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="callAtdDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Call} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_CALL_ATN")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="callAtnDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Call} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_CALL_DTN")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="callDtnDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Call} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_SETTINGS_DEFAULT_CARGO_PLANNED")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="cargoPlannedDefault"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Cargo} />} />
				</FormGroup>

				<FormGroup label={Language("STATUS_AFTER_DECLARING_VOYAGE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="afterDeclaringVoyage"
						control={control}
						render={({ field }) => <StatusSelect {...field} type={StatusType.Voyage} />} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default DefaultStatusSettingsDialog;
